/**
 * Reaction / emoji display: reactions may arrive as unicode ("🎉") or as Slack-style shortcodes ("tada",
 * ":tada:", "+1"). Unknown shortcodes render as ":name:". Content only — UI glyphs use the icon font.
 */
const SHORTCODES: Record<string, string> = {
  "+1": "👍", thumbsup: "👍", "-1": "👎", thumbsdown: "👎", heart: "❤️", tada: "🎉", rocket: "🚀", eyes: "👀",
  fire: "🔥", joy: "😂", smile: "😄", grin: "😁", laughing: "😆", wink: "😉", thinking_face: "🤔", thinking: "🤔",
  wave: "👋", clap: "👏", raised_hands: "🙌", pray: "🙏", ok_hand: "👌", muscle: "💪", "100": "💯", sparkles: "✨",
  white_check_mark: "✅", heavy_check_mark: "✔️", x: "❌", warning: "⚠️", bulb: "💡", memo: "📝", bug: "🐛",
  bee: "🐝", coffee: "☕", pizza: "🍕", ramen: "🍜", star: "⭐", zap: "⚡", party_popper: "🎉", slightly_smiling_face: "🙂",
  sob: "😭", sweat_smile: "😅", sunglasses: "😎", robot_face: "🤖", see_no_evil: "🙈", handshake: "🤝", brazil: "🇧🇷",
};

/** Unicode for a reaction key; already-unicode keys are returned unchanged. */
export function displayEmoji(key: string): string {
  const k = key.trim();
  // Anything outside ASCII is already an emoji (or other content) and is shown as-is.
  for (let i = 0; i < k.length; i++) if (k.charCodeAt(i) > 0x7f) return k;
  const name = k.replace(/^:|:$/g, "").toLowerCase();
  return SHORTCODES[name] ?? `:${name}:`;
}

/** Slack's skin tones (contracts SkinTone) → the Fitzpatrick modifier each adds; "none" adds nothing. */
export const SKIN_TONE_MODIFIERS: Readonly<Record<"none" | "light" | "medium-light" | "medium" | "medium-dark" | "dark", string>> = {
  none: "",
  light: "\u{1F3FB}",
  "medium-light": "\u{1F3FC}",
  medium: "\u{1F3FD}",
  "medium-dark": "\u{1F3FE}",
  dark: "\u{1F3FF}",
};

const MODIFIER = /[\u{1F3FB}-\u{1F3FF}]/u;
const MODIFIER_BASE = /^\p{Emoji_Modifier_Base}/u;

/**
 * `emoji` in my skin tone when it has skin (👍 + medium → 👍🏽, 🧑‍💻 → 🧑🏽‍💻); unchanged when it has none (🎉), already
 * carries a tone, or the tone is "none". Only the first person of a sequence is toned.
 */
export function withSkinTone(emoji: string, tone: keyof typeof SKIN_TONE_MODIFIERS): string {
  const mod = SKIN_TONE_MODIFIERS[tone];
  if (mod === "" || MODIFIER.test(emoji) || !MODIFIER_BASE.test(emoji)) return emoji;
  const first = String.fromCodePoint(emoji.codePointAt(0)!);
  // The modifier replaces a variation selector after the base (✋️ → ✋🏽).
  const rest = emoji.slice(first.length).replace(/^️/u, "");
  return first + mod + rest;
}

/** `emoji` without its skin tone (👍🏽 → 👍); unchanged when it has none. */
export function withoutSkinTone(emoji: string): string {
  return emoji.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, "");
}
